'use client'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Play, RefreshCw, Sparkles, ExternalLink, Rss } from 'lucide-react'
import { getRecoToken, type Subscription } from '@/lib/subscriptions'
import { usePlayer, type PlayerTrack } from '@/lib/player-context'
import { Button, CoverImg, EmptyState, Skeleton, ytThumb, cx } from '@/components/ui/kit'
import { SubAvatarImg } from './SubAvatars'
import Artwork from '@/components/Artwork'

/**
 * FIL des dernières vidéos / titres des abonnements.
 *  - sans sélection : /api/reco/video?token=…&kind=… (dernières sorties de toutes
 *    les chaînes suivies du type filtré), comme l'accueil Vidéo / Musique ;
 *  - avec une chaîne sélectionnée : /api/video/channel/<id> (ses uploads).
 * Les éléments sont groupés par date de publication.
 */
export type FeedItem = {
  videoId: string; title: string; channel: string; channelId: string
  thumbnail?: string; publishedAt?: string; publishedText?: string
}

function relTime(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso).getTime(); if (!d) return ''
  const s = Math.floor((Date.now() - d) / 1000)
  const u: [number, string][] = [[31536000, 'an'], [2592000, 'mois'], [604800, 'sem.'], [86400, 'j'], [3600, 'h'], [60, 'min']]
  for (const [sec, lbl] of u) { const n = Math.floor(s / sec); if (n >= 1) return `il y a ${n} ${lbl}${lbl === 'an' && n > 1 ? 's' : ''}` }
  return "à l'instant"
}

/** Libellé de groupe pour une date de publication. */
function groupLabel(iso?: string): string {
  const t = iso ? new Date(iso).getTime() : 0
  if (!t) return 'Plus ancien'
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const day = new Date(t); day.setHours(0, 0, 0, 0)
  const diff = Math.round((today.getTime() - day.getTime()) / 86400000)
  if (diff <= 0) return "Aujourd'hui"
  if (diff === 1) return 'Hier'
  if (diff < 7) return 'Cette semaine'
  if (diff < 14) return 'La semaine dernière'
  if (diff < 31) return 'Ce mois-ci'
  const d = new Date(t)
  const label = d.toLocaleDateString('fr-FR', { month: 'long', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export default function SubsFeed({ context, kind, selected, subs }: {
  context: 'music' | 'video'
  kind: 'all' | 'artist' | 'channel'
  selected: Subscription | null
  /** Abonnements visibles (sert au filtrage local et aux avatars). */
  subs: Subscription[]
}) {
  const router = useRouter()
  const { play } = usePlayer()
  const [items, setItems] = useState<FeedItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tick, setTick] = useState(0)
  const count = subs.length
  const selId = selected?.channelId || ''
  const selTitle = selected?.title || ''

  useEffect(() => {
    if (!selId && count === 0) { setItems([]); setLoading(false); setError(''); return }
    let dead = false
    setLoading(true); setError('')
    const bust = tick > 0 ? `&_=${tick}` : ''
    const req = selId
      ? fetch(`/api/video/channel/${encodeURIComponent(selId)}`).then(r => r.json()).then(j => {
          if (j?.error) throw new Error(j.error)
          return ((j.videos || []) as any[]).slice(0, 36).map(v => ({
            videoId: v.videoId, title: v.title, channel: j.title || selTitle, channelId: selId,
            thumbnail: v.thumbnail, publishedAt: v.publishedAt, publishedText: relTime(v.publishedAt),
          })) as FeedItem[]
        })
      : (() => {
          const token = getRecoToken()
          if (!token) return Promise.resolve([] as FeedItem[])
          return fetch(`/api/reco/video?token=${encodeURIComponent(token)}&kind=${kind}&limit=60&perChannel=4${bust}`)
            .then(r => r.json()).then(d => {
              if (d?.error && !(d.items || []).length) throw new Error(d.error)
              return (d.items || []) as FeedItem[]
            })
        })()
    req.then(list => { if (!dead) setItems(list) })
      .catch(() => { if (!dead) { setItems([]); setError('Impossible de charger les nouveautés pour le moment.') } })
      .finally(() => { if (!dead) setLoading(false) })
    return () => { dead = true }
  }, [selId, selTitle, kind, count, tick])

  // Le serveur peut avoir un temps de retard (désabonnement récent) → on filtre
  // sur les abonnements locaux visibles.
  const byId = useMemo(() => new Map(subs.map(s => [s.channelId, s])), [subs])
  const visible = useMemo(
    () => selId ? items : items.filter(v => !v.channelId || byId.has(v.channelId)),
    [items, byId, selId])

  const groups = useMemo(() => {
    const out: { label: string; items: FeedItem[] }[] = []
    for (const v of visible) {
      const label = groupLabel(v.publishedAt)
      const last = out[out.length - 1]
      if (last && last.label === label) last.items.push(v)
      else out.push({ label, items: [v] })
    }
    return out
  }, [visible])

  // Musique : les titres des ARTISTES se jouent en audio (comme l'accueil) ;
  // le reste ouvre le lecteur vidéo.
  const asTrack = (v: FeedItem): PlayerTrack => ({ videoId: v.videoId, title: v.title, artist: v.channel, thumbnail: v.thumbnail })
  const open = (v: FeedItem) => {
    const sub = byId.get(v.channelId) || (selected?.channelId === v.channelId ? selected : null)
    if (context === 'music' && sub?.type === 'artist') {
      const queue = visible.filter(x => byId.get(x.channelId)?.type === 'artist' || x.channelId === selId).map(asTrack)
      play(asTrack(v), queue)
    } else router.push(`/app/video/watch/${v.videoId}`)
  }
  const isAudio = (v: FeedItem) => context === 'music' && (byId.get(v.channelId)?.type === 'artist')

  return (
    <section data-reveal className="mt-8 md:mt-10">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="flex items-center gap-2.5 text-[22px] md:text-[28px] font-extrabold tracking-tight min-w-0">
          <Rss size={22} className="text-a3 shrink-0" />
          <span className="truncate">{selected ? `Nouveautés · ${selected.title}` : 'Le fil de tes abonnements'}</span>
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          {selected && (
            <Button variant="secondary" href={`/app/video/channel/${selected.channelId}`} icon={ExternalLink} className="!px-3.5 !py-2 hidden sm:inline-flex">
              La chaîne
            </Button>
          )}
          <button type="button" onClick={() => setTick(t => t + 1)} disabled={loading} title="Rafraîchir le fil" aria-label="Rafraîchir le fil"
            className="w-10 h-10 rounded-full grid place-items-center text-white/80 hover:text-white bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] transition disabled:opacity-50">
            <RefreshCw size={17} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {loading && visible.length === 0 ? (
        <div className="grid grid-cols-1 min-[520px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-4 gap-y-7">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <Skeleton className="aspect-video" />
              <div className="flex gap-3 mt-3"><Skeleton className="w-10 h-10 !rounded-full shrink-0" /><div className="flex-1 space-y-2"><Skeleton className="h-4 !rounded-md" /><Skeleton className="h-3 w-2/3 !rounded-md" /></div></div>
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState icon={error ? RefreshCw : Sparkles}
          title={error ? 'Fil indisponible' : 'Rien de nouveau pour le moment'}
          text={error || (selected
            ? 'Aucune vidéo récente trouvée pour cet abonnement.'
            : 'Les dernières sorties de tes abonnements apparaîtront ici.')}
          action={<Button variant="secondary" icon={RefreshCw} onClick={() => setTick(t => t + 1)}>Réessayer</Button>} />
      ) : (
        <div className="space-y-9 md:space-y-11">
          {groups.map(g => (
            <div key={g.label}>
              <h3 className="flex items-center gap-3 text-[18px] md:text-[21px] font-bold tracking-tight mb-4">
                {g.label}
                <span className="h-px flex-1 bg-white/[0.08]" />
                <span className="text-[13px] font-medium text-white/45">{g.items.length}</span>
              </h3>
              <div data-stagger data-reveal className="grid grid-cols-1 min-[520px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-4 md:gap-x-5 gap-y-7 md:gap-y-9">
                {g.items.map(v => {
                  const sub = byId.get(v.channelId) || selected
                  const audio = isAudio(v)
                  // Titre musical joué en AUDIO → vraie pochette CARRÉE (jamais l'image de la vidéo).
                  const asMusic = context === 'music' && (sub?.channelId === v.channelId) && sub?.type === 'artist'
                  return (
                    <div key={v.videoId} className="group min-w-0">
                      <button type="button" onClick={() => open(v)} className="block w-full text-left">
                        <span data-tilt className={cx('relative block rounded-2xl overflow-hidden bg-spanel3 ring-1 ring-white/[0.07] group-hover:ring-a3/60 transition', asMusic ? 'aspect-square blc-sq' : 'aspect-video')}>
                          {asMusic
                            ? <span className="absolute inset-0 pointer-events-none transition-transform duration-500 group-hover:scale-[1.04]"><Artwork track={asTrack(v)} fill strict size={320} rounded="rounded-none" /></span>
                            : <CoverImg videoId={v.videoId} src={v.thumbnail || ytThumb(v.videoId)} className="transition-transform duration-500 group-hover:scale-[1.04]" />}
                          <span className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                          <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition bg-black/25">
                            <span className="w-14 h-14 rounded-full grid place-items-center bg-gradient-to-br from-a2 to-a1 shadow-[0_0_24px_rgb(var(--a2)/.7)]"><Play size={22} className="fill-white text-white ml-0.5" /></span>
                          </span>
                          {v.publishedText && (
                            <span className="on-media absolute left-2 bottom-2 rounded-lg bg-black/65 backdrop-blur px-2 py-1 text-[12.5px] font-semibold text-white">
                              {v.publishedText}
                            </span>
                          )}
                          {audio && (
                            <span className="on-media absolute right-2 top-2 rounded-lg bg-a1/90 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-white">Audio</span>
                          )}
                        </span>
                      </button>
                      <div className="flex gap-3 mt-3">
                        {sub ? (
                          <button type="button" onClick={() => router.push(`/app/video/channel/${v.channelId}`)} title={sub.title} aria-label={`Chaîne ${sub.title}`} className="shrink-0 h-fit rounded-full ring-1 ring-white/10 hover:ring-a3 transition">
                            <SubAvatarImg sub={sub} size={40} />
                          </button>
                        ) : <span className="w-10 shrink-0" />}
                        <button type="button" onClick={() => open(v)} className="min-w-0 flex-1 text-left">
                          <span className={cx('block text-[16px] font-bold leading-snug line-clamp-2 group-hover:text-a4 transition')}>{v.title}</span>
                          <span className="block mt-0.5 text-[14px] text-white/60 truncate">{v.channel}</span>
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
